import { Message } from '../../types/chat';

// File type helpers
export const getFileIcon = (mimetype: string) => {
  if (mimetype.startsWith('image/')) return 'FileImageOutlined';
  if (mimetype.includes('pdf')) return 'FilePdfOutlined';
  if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('tar') || mimetype.includes('gz')) return 'FileZipOutlined';
  if (mimetype.includes('text') || mimetype.includes('doc') || mimetype.includes('office')) return 'FileTextOutlined';
  return 'FileOutlined';
};

// Type guard for fileInfo
export const isValidFileInfo = (fileInfo: Message['fileInfo']): fileInfo is NonNullable<Message['fileInfo']> => {
  return !!fileInfo && !!fileInfo.mimetype && !!fileInfo.pathToFile;
};

// Function to truncate file name while preserving extension
export const truncateFileName = (filename: string, maxLength = 25) => {
  if (!filename) return 'Unknown';
  if (filename.length <= maxLength) return filename;
  
  const lastDotIndex = filename.lastIndexOf('.');
  const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
  const name = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
  
  // Calculate how many characters to keep from the name
  const charsToKeep = maxLength - extension.length - 3; // 3 for the ellipsis
  
  if (charsToKeep <= 0) {
    // If name is too short, just truncate the extension part
    return `${name.substring(0, Math.min(maxLength - 3, name.length))}...`;
  }
  
  // For very long names, take some characters from the beginning and some from the end
  if (name.length > 30) {
    const firstPart = Math.ceil(charsToKeep * 0.6);
    const lastPart = charsToKeep - firstPart;
    
    if (lastPart > 0) {
      return `${name.substring(0, firstPart)}...${name.substring(name.length - lastPart)}${extension}`;
    }
  }
  
  return `${name.substring(0, charsToKeep)}...${extension}`;
};

// Function to parse timestamp to Date
export const parseTimestamp = (timestamp: string | number): Date => {
  let date: Date;
  
  try {
    // If it's a string
    if (typeof timestamp === 'string') {
      // If it's an empty string, use current time
      if (!timestamp.trim()) {
        return new Date();
      }
      
      // If it's a numeric string, convert to number
      if (/^\d+$/.test(timestamp)) {
        const num = parseInt(timestamp, 10);
        // If number is too small for milliseconds since 2000, it might be seconds
        if (num < 1000000000000) {
          date = new Date(num * 1000); // Convert seconds to milliseconds
        } else {
          date = new Date(num);
        }
      } 
      // If string contains date separators
      else if (timestamp.includes('T') || timestamp.includes('-') || timestamp.includes('/')) {
        date = new Date(timestamp);
      }
      // If no format matches, use current date
      else {
        console.warn('Unrecognized date format:', timestamp);
        date = new Date();
      }
    } 
    // If it's a number, use directly
    else if (typeof timestamp === 'number') {
      // Check if it's a 13-digit number (milliseconds) or 10-digit (seconds)
      if (timestamp < 1000000000000) {
        date = new Date(timestamp * 1000); // Convert seconds to milliseconds
      } else {
        date = new Date(timestamp);
      }
    }
    // For all other cases
    else {
      console.error('Unknown time format:', timestamp);
      date = new Date();
    }
    
    // Check if result is valid
    if (isNaN(date.getTime())) {
      console.error('Date is invalid after conversion:', date);
      return new Date(); // Return current time as fallback
    }
    
    return date;
  } catch (error) {
    console.error('Error parsing time:', error);
    return new Date(); // Return current time as fallback
  }
};

// Function to format timestamp
export const formatMessageTime = (timestamp: string) => {
  try {
    const date = parseTimestamp(timestamp);
    return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false});
  } catch (error) {
    console.error('Error formatting time:', timestamp, error);
    return '--:--';
  }
};

// Function to format full date for tooltip
export const formatFullDate = (timestamp: string) => {
  try {
    const date = parseTimestamp(timestamp);
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting full date:', timestamp, error);
    return 'Unknown Date';
  }
};

// Function to format date for headers
export const formatMessageDate = (timestamp: string) => {
  try {
    const date = parseTimestamp(timestamp);
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting message date:', timestamp, error);
    return 'Unknown Date';
  }
};

// Function to check if timestamp is within threshold (e.g., 5 minutes)
const isWithinTimeThreshold = (timestamp1: string, timestamp2: string, thresholdMinutes = 5): boolean => {
  const date1 = parseTimestamp(timestamp1);
  const date2 = parseTimestamp(timestamp2);
  
  // Calculate difference in milliseconds
  const diffMs = Math.abs(date1.getTime() - date2.getTime());
  // Convert to minutes
  const diffMinutes = diffMs / (1000 * 60);
  
  return diffMinutes <= thresholdMinutes;
};

// Function to group consecutive messages from the same sender within time threshold
export const groupConsecutiveMessages = (messages: Message[]): Message[][] => {
  if (!messages || messages.length === 0) return [];
  
  const groupedMessages: Message[][] = [];
  let currentGroup: Message[] = [messages[0]];
  
  for (let i = 1; i < messages.length; i++) {
    const currentMessage = messages[i];
    const previousMessage = messages[i - 1];
    
    // If sender is the same and within time threshold, add to current group
    if (
      currentMessage.sender === previousMessage.sender && 
      isWithinTimeThreshold(currentMessage.created, previousMessage.created)
    ) {
      currentGroup.push(currentMessage);
    } else {
      // Start a new group
      groupedMessages.push([...currentGroup]);
      currentGroup = [currentMessage];
    }
  }
  
  // Add the last group
  if (currentGroup.length > 0) {
    groupedMessages.push(currentGroup);
  }
  
  return groupedMessages;
};

// Function to group messages by date
export const groupMessagesByDate = (messages: Message[]) => {
  const grouped: { [date: string]: Message[] } = {};
  
  messages?.forEach(msg => {
    const date = formatMessageDate(msg.created);
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(msg);
  });
  
  return grouped;
};

// Check if message has formatted content
export const hasFormattedContent = (msg: Message): boolean => {
  return !!(
    msg.format && msg.formatted_body || 
    (msg.content && typeof msg.content === 'object' && 
     (msg.content as any).format && (msg.content as any).formatted_body)
  );
};

/**
* Разбирает входящее сообщение вида
*   <b>admin:Admin</b><br />test
*   <b>Olimp:Video 2</b><br />строка 1<br/>строка 2
* и возвращает объект { username, messageContent }.
* Если строка не соответствует формату — возвращает false.
*
* Работает и в Node.js, и в браузере.
*/
export const parseRichMessage = (raw: string) => {
  // Проверяем, начинается ли строка с нужного шаблона
  //    <b>…</b><br> или <br/>
  const re = /^<b>([^<]+)<\/b><br\s*\/?>/i;

  const m = raw.match(re);
  if (!m) return false;                 // просто текст — сразу false

  const username = m[1].trim();         // админскую часть забрали
  let rest = raw.slice(m[0].length);    // остальной текст после <br>

  // Приводим все варианты <br>, <br/>, <br /> к переводу строки «\n»
  rest = rest.replace(/<br\s*\/?>/gi, '\n').trim();

  return { username, messageContent: rest };
}

// Get message text content accounting for different message structures
export const getMessageContent = (msg: Message): string => {
  // Handle Matrix message structure where content is an object
  if (msg.content && typeof msg.content === 'object') {
    if(msg.content.m_new_content) {
      return msg.content.m_new_content.body || '';
    } else {
      const contentObj = msg.content as any;
      return contentObj.body || '';
    }
  }
  // Standard plain text content
  return typeof msg.content === 'string' ? msg.content : '';
};

// Get original message text for editing (strips HTML formatting)
export const getOriginalMessageText = (msg: Message): string => {
  const content = getMessageContent(msg);
  
  // If it's HTML formatted content with our specific format, extract the original text
  if (content && typeof content === 'string') {
    // Check for the pattern: <b>username:</b> text
    const usernamePattern = /<b>@([a-zA-Z0-9_-]+):<\/b>\s*([\s\S]*)/;
    const match = content.match(usernamePattern);
    
    if (match && match.length >= 3) {
      // Reconstruct the original text: @username: + message
      const username = match[1];
      const messageText = match[2];
      return `@${username}: ${messageText}`;
    }
    
    // Check for other HTML patterns and strip HTML tags
    if (content.includes('<') && content.includes('>')) {
      return stripHtmlTags(content);
    }
  }
  
  return content;
};

// Get formatted body content
export const getFormattedBody = (msg: Message): string => {
  // Handle Matrix message structure
  if (msg.content && typeof msg.content === 'object') {
    const contentObj = msg.content as any;
    return contentObj.body || '';
  }
  // Standard formatted body
  return msg.formatted_body || '';
};

// Get format type
export const getFormat = (msg: Message): string => {
  // Handle Matrix message structure
  if (msg.content && typeof msg.content === 'object') {
    const contentObj = msg.content as any;
    return contentObj.format || '';
  }
  // Standard format
  return msg.format || '';
};

// Process HTML content to handle checkbox lists correctly
export const processFormattedContent = (html: string): string => {
  // Replace Matrix-style checkboxes with actual HTML checkboxes
  return html
    .replace(/\[x\]/g, '<input type="checkbox" checked disabled />')
    .replace(/\[ \]/g, '<input type="checkbox" disabled />');
};

// Function to extract username and message from HTML formatted content
export const extractUsernameAndMessage = (content: string): { username: string | null, localUsername: string | null, message: string } => {
  // Check if content is HTML with bold username
  if (content && typeof content === 'string') {
    // Новый формат с объединённым тегом: "<b>Admin:Test</b><br />Message"
    const combinedFormatRegex = /<b>(.*?):(.*?)<\/b><br\s*\/?>([\s\S]*)/;
    const combinedMatch = content.match(combinedFormatRegex);
    
    if (combinedMatch && combinedMatch.length >= 4) {
      return {
        username: combinedMatch[1],     // Prefix (Admin or USER_LOGIN)
        localUsername: combinedMatch[2], // Actual username
        message: combinedMatch[3]        // Message text
      };
    }
    
    // Старый формат "<b>SERVER_USER</b>:<b>LocalUsername</b><br />Message"
    const regex = /<b>(.*?)<\/b>:<b>(.*?)<\/b><br\s*\/?>([\s\S]*)/;
    const match = content.match(regex);
    
    if (match && match.length >= 4) {
      return {
        username: match[1],         // SERVER_USER
        localUsername: match[2],    // LocalUsername (фактический отправитель)
        message: match[3]           // Текст сообщения
      };
    }
    
    // Еще более старый формат "<b>Username</b><br />Message"
    const oldFormatRegex = /<b>(.*?)<\/b><br\s*\/?>([\s\S]*)/;
    const oldFormatMatch = content.match(oldFormatRegex);
    
    if (oldFormatMatch && oldFormatMatch.length >= 3) {
      return {
        username: oldFormatMatch[1],
        localUsername: null,
        message: oldFormatMatch[2]
      };
    }
  }
  
  // Return original content if no match
  return {
    username: null,
    localUsername: null,
    message: content
  };
};

// Function to truncate message text
export const truncateMessageText = (text: string, maxLength = 40): string => {
  if (!text) return 'Нет сообщений';
  
  // Удаляем HTML-теги перед обрезкой
  const plainText = stripHtmlTags(text);
  
  if (plainText.length <= maxLength) return plainText;
  
  return `${plainText.substring(0, maxLength - 3)}...`;
};

// Function to strip HTML tags from a string
export const stripHtmlTags = (htmlText: string): string => {
  if (!htmlText) return '';
  
  // Заменяем <br> и <br/> на пробел
  let text = htmlText.replace(/<br\s*\/?>/gi, ' ');
  
  // Заменяем все остальные HTML-теги на пустую строку
  text = text.replace(/<\/?[^>]+(>|$)/g, '');
  
  // Декодируем HTML-сущности (например, &amp; -> &)
  text = text.replace(/&amp;/g, '&')
             .replace(/&lt;/g, '<')
             .replace(/&gt;/g, '>')
             .replace(/&quot;/g, '"')
             .replace(/&#39;/g, "'");
  
  return text;
};

// Check if a message is a redaction/deletion
export const isRedactionMessage = (message: Message): boolean => {
  return message.type === 'm.room.redaction';
};

// Find a target message that was redacted
export const findRedactedMessage = (messages: Message[], redaction: Message): Message | undefined => {
  if (!isRedactionMessage(redaction) || !redaction.content || typeof redaction.content !== 'object') {
    return undefined;
  }
  
  // The m_relates_to field contains the event_id of the redacted message
  const targetEventId = redaction.content.m_relates_to;
  if (!targetEventId) return undefined;
  
  return messages.find(msg => msg.event_id === targetEventId);
};

// Check if a message has been redacted
export const hasBeenRedacted = (message: Message, allMessages: Message[]): boolean => {
  if (!message.event_id) return false;
  
  return allMessages.some(msg => 
    isRedactionMessage(msg) && 
    typeof msg.content === 'object' && 
    msg.content?.m_relates_to === message.event_id
  );
};

// Список типов сервисных сообщений
export const SERVICE_MESSAGE_TYPES = [
  'm.room.create',
  'm.room.member',
  'm.room.name',
  'm.room.join_rules',
  'm.room.topic',
  'm.room.history_visibility',
  'm.room.guest_access'
];

// Проверка, является ли сообщение сервисным
export const isServiceMessage = (message: Message): boolean => {
  return SERVICE_MESSAGE_TYPES.includes(message.type || '');
};

// Проверка, является ли сообщение реакцией
export const isReactionMessage = (message: Message): boolean => {
  return message.type === 'm.reaction';
};

// Проверка, является ли сообщение обычным текстовым сообщением
export const isRegularMessage = (message: Message): boolean => {
  return message.type === 'm.room.message' || 
        !message.type || // Для обратной совместимости
        (!isServiceMessage(message) && !isReactionMessage(message) && !isRedactionMessage(message));
};

// Проверка, является ли сообщение отредактированным (замененным)
export const isEditMessage = (message: Message): boolean => {
  // Проверяем, есть ли m_new_content и m_relates_to с rel_type: m.replace
  if (typeof message.content !== 'object') return false;
  
  const hasNewContent = !!message.content.m_new_content;
  const hasRelatesTo = message.content.m_new_content?.m_relates_to?.rel_type === 'm.replace';
  
  // Характерный признак - сообщение начинается с * 
  const bodyStartsWithAsterisk = typeof message.content.body === 'string' && 
                               message.content.body.startsWith('* ');
  
  return (hasNewContent && hasRelatesTo) || bodyStartsWithAsterisk;
};

// Получить идентификатор исходного сообщения для отредактированного сообщения
export const getOriginalMessageId = (editMessage: Message): string | null => {
  if (!isEditMessage(editMessage) || typeof editMessage.content !== 'object') 
    return null;
  
  return editMessage.content.m_new_content?.m_relates_to?.event_id || null;
};

// Получить новое содержимое для сообщения из сообщения с редактированием
export const getNewMessageContent = (editMessage: Message): any => {
  if (!isEditMessage(editMessage) || typeof editMessage.content !== 'object') 
    return null;
  
  return editMessage.content.m_new_content || null;
};

// Применить редактирование к сообщению
export const applyMessageEdit = (original: Message, edit: Message): Message => {
  if (!isEditMessage(edit) || typeof edit.content !== 'object') return original;
  
  const newContent = getNewMessageContent(edit);
  if (!newContent) return original;
  
  // Создаем глубокую копию оригинального сообщения
  const updatedMessage = JSON.parse(JSON.stringify(original));
  
  // Если у нас контент в виде объекта
  if (typeof updatedMessage.content === 'object') {
    // Обновляем body и другие поля, сохраняя структуру
    updatedMessage.content = {
      ...updatedMessage.content,
      body: newContent.body || updatedMessage.content.body,
      formatted_body: newContent.formatted_body || updatedMessage.content.formatted_body || '',
      format: newContent.format || updatedMessage.content.format || '',
      msgtype: newContent.msgtype || updatedMessage.content.msgtype,
      // Добавляем флаг, что сообщение было отредактировано
      has_been_edited: true,
      edit_timestamp: edit.created
    };
  } else {
    // Если контент - строка, создаем объект контента
    updatedMessage.content = {
      body: newContent.body || updatedMessage.content,
      msgtype: newContent.msgtype || "m.text",
      has_been_edited: true,
      edit_timestamp: edit.created
    };
  }
  
  return updatedMessage;
};

// Проверка, является ли сообщение отредактированным
export const hasBeenEdited = (message: Message): boolean => {
  if (typeof message.content !== 'object') return false;
  return !!message.content.has_been_edited;
};

// Обновить список сообщений, применяя все редактирования
export const applyAllEdits = (messages: Message[]): Message[] => {
  // Создаем копию сообщений для обработки
  const updatedMessages: Message[] = JSON.parse(JSON.stringify(messages));
  // Создаем map для быстрого поиска сообщений по event_id
  const messageMap = new Map<string, { index: number, message: Message }>();
  
  // Заполняем map
  updatedMessages.forEach((msg, index) => {
    if (msg.event_id) {
      messageMap.set(msg.event_id, { index, message: msg });
    }
  });
  
  // Находим все сообщения-редактирования
  const editMessages = messages.filter(msg => isEditMessage(msg));
  // Сортируем редактирования по времени, чтобы применять их в хронологическом порядке
  editMessages.sort((a, b) => {
    const timeA = typeof a.created === 'string' ? parseInt(a.created) : 0;
    const timeB = typeof b.created === 'string' ? parseInt(b.created) : 0;
    return timeA - timeB;
  });
  
  // Применяем редактирования
  editMessages.forEach(editMsg => {
    const originalId = getOriginalMessageId(editMsg);
    if (!originalId) return;
    
    const original = messageMap.get(originalId);
    if (!original) return;
    
    // Применяем редактирование
    const updatedMessage = applyMessageEdit(original.message, editMsg);
    updatedMessages[original.index] = updatedMessage;
    
    // Обновляем map с новой версией сообщения
    messageMap.set(originalId, { index: original.index, message: updatedMessage });
  });
  
  // Фильтруем из результата сообщения-редактирования, но оставляем оригинальные
  return updatedMessages.filter(msg => !isEditMessage(msg));
};

// Обновляем функцию getVisibleMessages чтобы исключать edit-сообщения
export const getVisibleMessages = (messages: Message[]): Message[] => {
  // Сначала применяем все редактирования
  const messagesWithEditsApplied = applyAllEdits(messages);
  
  // Затем фильтруем служебные сообщения
  return messagesWithEditsApplied.filter(msg => 
    msg.type !== 'm.reaction' && 
    msg.type !== 'm.room.redaction' &&
    !isEditMessage(msg)
  );
};

// Проверка, является ли редакция удалением реакции
export const isReactionRedaction = (redaction: Message, allMessages: Message[]): boolean => {
  if (!isRedactionMessage(redaction)) return false;
  
  // Находим удаляемое сообщение
  const redactedMessage = findRedactedMessage(allMessages, redaction);
  
  // Проверяем, является ли удаляемое сообщение реакцией
  return redactedMessage ? isReactionMessage(redactedMessage) : false;
}; 

// Вспомогательная функция для преобразования пользовательского текста в HTML
export const textToHtml = (text: string) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  // 1. Сначала экранируем HTML-символы для безопасности
  // ВАЖНО: & должно быть первым, иначе мы повредим другие escape-последовательности
  let htmlText = text
    .replace(/&/g, '&amp;')   // & -> &amp;
    .replace(/</g, '&lt;')    // < -> &lt;
    .replace(/>/g, '&gt;')    // > -> &gt;
    .replace(/"/g, '&quot;')  // " -> &quot;
    .replace(/'/g, '&#39;');  // ' -> &#39;
  
  // 2. Заменяем специальные символы на HTML-эквиваленты
  htmlText = htmlText
    .replace(/\r\n/g, '<br/>')         // Windows переносы строк (\r\n)
    .replace(/\n/g, '<br/>')           // Unix переносы строк (\n)
    .replace(/\r/g, '<br/>');          // Mac переносы строк (\r)
  
  // 3. Дополнительные преобразования (легко расширяемые)
  htmlText = htmlText
    .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')  // Табуляция -> 4 неразрывных пробела
    .replace(/  /g, '&nbsp;&nbsp;');             // Двойные пробелы -> неразрывные пробелы
  
  // 4. Поддержка простой разметки (опционально, можно расширить)
  // Markdown-подобный синтаксис для базового форматирования
  htmlText = htmlText
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // **жирный**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')              // *курсив*
    .replace(/`(.*?)`/g, '<code>$1</code>')            // `код`
    .replace(/~~(.*?)~~/g, '<del>$1</del>');           // ~~зачеркнутый~~
  
  // 5. Дополнительные удобства (легко добавить новые)
  htmlText = htmlText
    .replace(/\^(\w+)/g, '<sup>$1</sup>')              // ^надстрочный
    .replace(/_(\w+)_/g, '<sub>$1</sub>');             // _подстрочный
  
  // 6. Автолинки (простая версия для URL)
  htmlText = htmlText
    .replace(/(https?:\/\/[^\s<>'"]+)/g, '<a href="$1" target="_blank">$1</a>');
  
  return htmlText;
}

// Расширенная функция для логирования преобразования (для отладки)
export const logTextTransformation = (originalText: string, htmlText: string, username: string) => {
  console.log(`📝 Text transformation for ${username}:`);
  console.log(`Original: ${JSON.stringify(originalText)}`);
  console.log(`HTML: ${JSON.stringify(htmlText)}`);
  
  // Показываем статистику преобразований
  const transformations = [];
  if (htmlText.includes('<br/>')) transformations.push('переносы строк');
  if (htmlText.includes('<strong>')) transformations.push('жирный текст');
  if (htmlText.includes('<em>')) transformations.push('курсив');
  if (htmlText.includes('<code>')) transformations.push('код');
  if (htmlText.includes('<del>')) transformations.push('зачеркнутый');
  if (htmlText.includes('&lt;') || htmlText.includes('&gt;')) transformations.push('экранирование HTML');
  if (htmlText.includes('<a href=')) transformations.push('автолинки');
  
  if (transformations.length > 0) {
    console.log(`📊 Applied transformations: ${transformations.join(', ')}`);
  }
}

/**
 * Преобразует первый символ текста в верхний регистр
 * @param text Входной текст
 * @returns Текст с первой заглавной буквой
 */
export const capitalizeText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Преобразует первый символ текста в нижний регистр
 * @param text Входной текст
 * @returns Текст с первой строчной буквой
 */
export const lowercaseText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return text.charAt(0).toLowerCase() + text.slice(1);
};